/**
 * QuickBooks Online integration card for Settings → Integrations.
 *
 * - Polls /api/qbo/status on mount to decide whether to show "Not configured",
 *   "Connect your workspace", or a list of active connections.
 * - On click: calls /api/qbo/connect, receives the Intuit authorize_url, and
 *   redirects the browser there. Intuit redirects back to /api/qbo/oauth/callback
 *   which stores the tokens and bounces to /app/settings?tab=integrations.
 * - Shows last sync status + "Sync now" button + Disconnect per connection.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Trash2, Loader2, Zap, AlertTriangle, CheckCircle2 } from "lucide-react";

function fmt(dt) {
  if (!dt) return "never";
  try {
    const d = new Date(dt);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return "—"; }
}

export default function QBOCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncingId, setSyncingId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/qbo/status");
      setStatus(r.data);
    } catch (e) {
      // Non-fatal — the server might be mid-restart.
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Surface the OAuth-callback flash into the UI once.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("qbo_connected")) {
      toast.success("QuickBooks connected");
      params.delete("qbo_connected"); params.delete("conn");
      window.history.replaceState({}, "", window.location.pathname + (params.toString() ? "?" + params.toString() : ""));
      load();
    } else if (params.get("qbo_error")) {
      toast.error(`QuickBooks: ${params.get("qbo_error")}`);
      params.delete("qbo_error");
      window.history.replaceState({}, "", window.location.pathname + (params.toString() ? "?" + params.toString() : ""));
    }
  }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const r = await api.get("/qbo/connect");
      if (r.data?.authorize_url) {
        window.location.href = r.data.authorize_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start QuickBooks connect flow");
      setConnecting(false);
    }
  };

  const syncNow = async (id) => {
    setSyncingId(id);
    try {
      await api.post(`/qbo/connections/${id}/sync`);
      toast.success("Sync queued — new rows will appear in QuickBooks in a moment");
      // Give the background task a nudge then refresh so last_sync_at updates.
      setTimeout(load, 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed to queue");
    } finally { setSyncingId(null); }
  };

  const disconnect = async (id) => {
    if (!window.confirm("Disconnect this QuickBooks company? The data already pushed will stay in QuickBooks.")) return;
    try {
      await api.delete(`/qbo/connections/${id}`);
      toast.success("Disconnected");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not disconnect");
    }
  };

  if (loading) {
    return (
      <div className="border p-5 flex items-center gap-2 text-sm text-[var(--ll-ink-muted)]" style={{ borderColor: "var(--ll-border)" }} data-testid="integration-qbo-loading">
        <Loader2 size={14} className="animate-spin" /> Loading QuickBooks status…
      </div>
    );
  }

  const configured = !!status?.configured;
  const conns = status?.connections || [];

  return (
    <div className="border p-5 flex flex-col gap-3" style={{ borderColor: "var(--ll-border)" }} data-testid="integration-qbo">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold">QuickBooks Online</div>
          <div className="text-[11px] uppercase tracking-widest text-[var(--ll-ink-muted)] mt-0.5">
            Accounting · One-way push · {status?.environment || "sandbox"}
          </div>
        </div>
        {configured ? (
          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 text-white" style={{ background: "#10B981" }}>Ready</span>
        ) : (
          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-[var(--ll-bg-card)] border" style={{ borderColor: "var(--ll-border)" }}>Setup required</span>
        )}
      </div>
      <p className="text-xs text-[var(--ll-ink-soft)] leading-relaxed">
        Push your chart of accounts, bank accounts, customers, vendors, invoices, and categorized transactions into QuickBooks. Each client workspace connects its own QBO company.
      </p>

      {!configured && (
        <div className="text-xs bg-[#FFFBEB] border px-3 py-2 flex items-start gap-2" style={{ borderColor: "#F59E0B" }} data-testid="qbo-not-configured">
          <AlertTriangle size={14} className="text-[#B45309] shrink-0 mt-0.5" />
          <div>
            An admin needs to paste Intuit app credentials into the server env (<code>QBO_CLIENT_ID</code>, <code>QBO_CLIENT_SECRET</code>, <code>QBO_REDIRECT_URI</code>, <code>QBO_TOKEN_ENCRYPTION_KEY</code>) before this card can be used.
            {status?.suggested_encryption_key && (
              <div className="mt-1 font-mono-fin text-[10px] break-all">
                Suggested encryption key: <strong>{status.suggested_encryption_key}</strong>
              </div>
            )}
          </div>
        </div>
      )}

      {configured && (
        <button
          type="button"
          onClick={connect}
          disabled={connecting}
          data-testid="integration-qbo-connect"
          className="self-start text-xs px-3 py-1.5 bg-[var(--ll-ink)] text-white hover:bg-[#2D6A4F] flex items-center gap-1.5 disabled:opacity-60"
        >
          {connecting ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
          {connecting ? "Redirecting…" : "Connect QuickBooks workspace"}
        </button>
      )}

      {conns.length > 0 && (
        <div className="border-t pt-3 mt-1 space-y-2" style={{ borderColor: "var(--ll-border)" }} data-testid="qbo-connections-list">
          <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">
            {conns.length === 1 ? "Connected QuickBooks company" : `${conns.length} connected QuickBooks companies`}
          </div>
          {conns.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-3 bg-[var(--ll-bg-card)] px-3 py-2" data-testid={`qbo-conn-${c.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate flex items-center gap-1.5">
                  {c.status === "connected" ? <CheckCircle2 size={13} className="text-[#10B981]" /> : <AlertTriangle size={13} className="text-[#B45309]" />}
                  {c.company_name || `Realm ${c.realm_id}`}
                </div>
                <div className="text-[11px] text-[var(--ll-ink-soft)]">
                  Last synced {fmt(c.last_sync_at)}
                  {c.last_sync_error && <span className="text-[var(--ll-error)]"> · {c.last_sync_error}</span>}
                </div>
              </div>
              <button
                type="button"
                onClick={() => syncNow(c.id)}
                disabled={syncingId === c.id}
                data-testid={`qbo-conn-sync-${c.id}`}
                className="text-[11px] px-2.5 py-1 border hover:bg-[var(--ll-mint)] flex items-center gap-1 disabled:opacity-60"
                style={{ borderColor: "var(--ll-border)" }}
              >
                <RefreshCw size={11} className={syncingId === c.id ? "animate-spin" : ""} />
                Sync now
              </button>
              <button
                type="button"
                onClick={() => disconnect(c.id)}
                data-testid={`qbo-conn-disconnect-${c.id}`}
                className="text-[11px] p-1 hover:bg-[var(--ll-error)] hover:text-white"
                title="Disconnect"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
